import { useEffect, useRef, useState } from 'react';
import type { Scene, StudioState } from '@liveforge/shared';
import { errorMessage } from '../lib/api';
import { getLive, getShow, updateLive } from '../lib/resources';
import { studioState, useStudioStore } from '../stores/studio-store';

/**
 * Keeps a Live in the database instead of only in this tab, so a refresh or a
 * dropped connection is an interruption rather than a lost broadcast.
 *
 * Two columns, because they are two different things. `scene_override` is the
 * composition - what the Live is made of - and starts from the Show's preset,
 * which is what a null override means; the first save is what ends the
 * inheritance. `studio_state` is what the director was *doing*: the scene on
 * air, the one in preview, the mode, the transition, and a manifest of the
 * sources that were feeding it.
 *
 * Neither is ever written back to the Show. A Show is a template, and what was
 * on air at 21:04 on one night is not part of it.
 */

/**
 * Long, because each of these is a database write rather than a frame. A drag
 * across the preview produces a state change per pixel; the relay wants those
 * at 50ms so the picture keeps up, and Postgres wants none of them.
 */
const SAVE_DEBOUNCE_MS = 1200;

export function useStudioPersistence(liveId: string | undefined) {
  const scenes = useStudioStore((state) => state.scenes);
  const hydratedFor = useStudioStore((state) => state.hydratedFor);
  // Each is subscribed to separately so the save fires on a change to any of
  // them, and studioState() is only rebuilt when one actually moved.
  const mode = useStudioStore((state) => state.mode);
  const activeSceneId = useStudioStore((state) => state.activeSceneId);
  const previewSceneId = useStudioStore((state) => state.previewSceneId);
  const transition = useStudioStore((state) => state.transition);
  const attachedSources = useStudioStore((state) => state.attachedSources);
  const pendingSources = useStudioStore((state) => state.pendingSources);

  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  /**
   * What the server is known to hold, seeded by the load with the very values
   * it read - so hydrating does not immediately save back what just arrived.
   *
   * The scenes are compared by identity because the store replaces the array
   * on every edit. The studio state cannot be: it is rebuilt on each render
   * from six separate fields, so it is compared as JSON. Both are small.
   */
  const savedRef = useRef<{ scenes: Scene[]; studio: string } | null>(null);
  /** Read by the unmount flush, which must not re-run when the scenes change. */
  const pendingRef = useRef<{ liveId: string; scenes: Scene[]; studio: StudioState } | null>(null);

  useEffect(() => {
    if (!liveId) return;

    // Synchronous, before the first await: from here until the load lands the
    // save gate is shut, so a Live opened after another one cannot be written
    // with the scenes still sitting in this singleton store.
    useStudioStore.getState().unhydrate();
    savedRef.current = null;
    pendingRef.current = null;

    const controller = new AbortController();
    let cancelled = false;

    void (async () => {
      try {
        const live = await getLive(liveId, controller.signal);
        // Null override means the Live has never been edited and still follows
        // its Show. Reading the preset here is what makes the inheritance real
        // rather than a column comment.
        const preset =
          live.sceneOverride ?? (await getShow(live.showId, controller.signal)).scenePreset;
        if (cancelled) return;
        useStudioStore.getState().load(liveId, preset, live.studioState);
        // Read back rather than echoed: load() repairs a stored scene id that
        // no longer names a scene, and the repair has to be saved, not skipped
        // as "unchanged".
        savedRef.current = { scenes: preset, studio: JSON.stringify(live.studioState) };
        // Cleared here rather than at the top of the effect: a synchronous
        // setState in an effect body is the one lint rule this codebase has
        // downgraded rather than fixed, and there is no reason to add to it.
        setLoadError(null);
      } catch (cause) {
        if (cancelled || controller.signal.aborted) return;
        // The gate stays shut. A studio that could not read the scenes must
        // not then write its empty default over them.
        setLoadError(errorMessage(cause));
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [liveId]);

  useEffect(() => {
    if (!liveId || hydratedFor !== liveId) return;

    const studio = studioState(useStudioStore.getState());
    const encoded = JSON.stringify(studio);
    const saved = savedRef.current;
    if (saved && saved.scenes === scenes && saved.studio === encoded) return;

    pendingRef.current = { liveId, scenes, studio };

    const timer = setTimeout(() => {
      void updateLive(liveId, { sceneOverride: scenes, studioState: studio })
        .then(() => {
          savedRef.current = { scenes, studio: encoded };
          if (pendingRef.current?.scenes === scenes) {
            pendingRef.current = null;
          }
          setSaveError(null);
        })
        .catch((cause: unknown) => setSaveError(errorMessage(cause)));
    }, SAVE_DEBOUNCE_MS);

    return () => clearTimeout(timer);
    // The six studio fields are read through getState() rather than used
    // directly, but they are what makes this re-run: subscribing to each is
    // how a change to any one of them reaches the save.
  }, [
    liveId,
    hydratedFor,
    scenes,
    mode,
    activeSceneId,
    previewSceneId,
    transition,
    attachedSources,
    pendingSources,
  ]);

  // Leaving the studio cancels the debounce mid-flight, which would drop the
  // last edit a director made before clicking away. The navigation is
  // in-page, so the request outlives the component and completes.
  useEffect(
    () => () => {
      const pending = pendingRef.current;
      if (!pending) return;
      pendingRef.current = null;
      void updateLive(pending.liveId, {
        sceneOverride: pending.scenes,
        studioState: pending.studio,
      }).catch(() => {
        // Nothing can be shown: the studio it belonged to is already gone.
      });
    },
    [],
  );

  return { loadError, saveError };
}
